import {
  trackAppInsightsError,
  trackAppInsightsEvent,
  sanitizeStringUrls,
} from '../../initAppInsights';

// Type definitions for error handling

interface LogPayloadData {
  // File information
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  contentType?: string;

  // Upload context
  uploadMethod?: string;
  uploadStep?: string;
  uploadDuration?: number;

  // Error information
  errorType?: string;
  errorName?: string;
  errorMessage?: string;

  // HTTP response details (when available)
  statusCode?: number;
  statusText?: string;
  responseBody?: string;
  responseHeaders?: Record<string, string>;

  // Multipart upload specific
  uploadId?: string;
  partNumber?: number;
  totalParts?: number;
  batchNumber?: number;

  // Additional context
  objectKey?: string;
  presignedUrl?: string;

  // Browser/environment info
  userAgent?: string;
  event?: string;

  // Allow additional properties
  [key: string]: unknown;
}

interface HttpErrorDetails {
  requestUrl: string | undefined;
  statusCode: number | undefined;
  statusText: string | undefined;
  responseBody: string | undefined;
  responseHeaders: Record<string, string> | undefined;
}

const diagnosticString = (value: unknown): string | undefined =>
  typeof value === 'string'
    ? sanitizeStringUrls(value).slice(0, 1024)
    : undefined;

export interface UploadDiagnosticContext {
  bytesUploaded?: number;
  hadUploadProgress?: boolean;
  presignedUrl?: string | null;
  uploadStep?: string;
  originalErrorName?: string;
  originalErrorMessage?: string;
  errorDetails?: string;
  causeName?: string;
  causeMessage?: string;
  requestUrl?: string;
  statusCode?: number;
  statusOrigin?: string;
  statusText?: string;
  transportOutcome?: string;
}

const isXhr = (value: any) =>
  value &&
  ((typeof XMLHttpRequest !== 'undefined' && value instanceof XMLHttpRequest) ||
    typeof value.readyState === 'number' ||
    typeof value.responseURL === 'string');

// Read one error and one immediate cause; never retain a request/response graph.
export const extractUploadErrorDiagnostics = (
  error: unknown,
  uploadResponse?: unknown
): UploadDiagnosticContext => {
  const details = error && typeof error === 'object' ? (error as any) : {};
  const cause = details.cause;
  const syntheticTimeout =
    details.message === 'Request has expired' &&
    details.source?.status === 403 &&
    !isXhr(details.source) &&
    Object.keys(details.source).length === 1;
  const result: UploadDiagnosticContext = {
    originalErrorName: diagnosticString(details.name),
    originalErrorMessage: diagnosticString(details.message),
    errorDetails: diagnosticString(details.details),
    causeName: diagnosticString(cause?.name),
    causeMessage: diagnosticString(cause?.message),
    transportOutcome:
      syntheticTimeout || details.name === 'TimeoutError'
        ? 'timeout'
        : details.name === 'AbortError'
        ? 'abort'
        : details.message === 'Unknown error' && isXhr(details.source)
        ? 'error'
        : 'unknown',
  };
  const candidates = [
    {
      value: details.source,
      origin: isXhr(details.source) ? 'xhr' : 'library',
    },
    { value: uploadResponse, origin: 'response' },
    { value: details.response, origin: 'response' },
    {
      value: details.request,
      origin: isXhr(details.request) ? 'xhr' : 'request',
    },
    { value: cause?.source, origin: isXhr(cause?.source) ? 'xhr' : 'library' },
    { value: cause?.response, origin: 'response' },
    {
      value: cause?.request,
      origin: isXhr(cause?.request) ? 'xhr' : 'request',
    },
  ];
  const response = candidates.find(
    ({ value }) => typeof value?.status === 'number'
  );
  if (response) {
    result.statusCode = response.value.status;
    result.statusOrigin = response.origin;
    result.statusText = diagnosticString(response.value.statusText);
  } else if (typeof details.message === 'string') {
    const match = details.message.match(/status code (\d+)/i);
    if (match) {
      result.statusCode = parseInt(match[1], 10);
      result.statusOrigin = 'library';
    }
  }
  result.requestUrl = diagnosticString(
    details.config?.url ??
      details.response?.config?.url ??
      details.source?.responseURL ??
      details.request?.responseURL ??
      (uploadResponse as any)?.url ??
      details.response?.request?.responseURL ??
      details.response?.url ??
      cause?.config?.url ??
      cause?.source?.responseURL ??
      cause?.request?.responseURL ??
      cause?.response?.url
  );
  return Object.fromEntries(
    Object.entries(result).filter(([, value]) => value !== undefined)
  );
};

// Retain the legacy helper contract for its existing callers. Emitted upload
// properties below intentionally exclude response bodies and headers.
export const extractHttpErrorDetails = (error: unknown): HttpErrorDetails => {
  const { requestUrl, statusCode, statusText } =
    extractUploadErrorDiagnostics(error);
  const response = (error as any)?.response;
  return {
    requestUrl,
    statusCode,
    statusText,
    responseBody: response?.data,
    responseHeaders: response?.headers,
  };
};

const uploadContextProperties = (error: unknown): UploadDiagnosticContext => {
  const context = (error as any)?.uploadContext;
  if (!context) return {};
  const result: UploadDiagnosticContext = {};
  ['bytesUploaded', 'statusCode'].forEach((key) => {
    if (typeof context[key] === 'number') result[key] = context[key];
  });
  if (typeof context.hadUploadProgress === 'boolean')
    result.hadUploadProgress = context.hadUploadProgress;
  [
    'presignedUrl',
    'uploadStep',
    'originalErrorName',
    'originalErrorMessage',
    'errorDetails',
    'causeName',
    'causeMessage',
    'requestUrl',
    'statusOrigin',
    'statusText',
    'transportOutcome',
  ].forEach((key) => {
    const value = diagnosticString(context[key]);
    if (value !== undefined) result[key] = value;
  });
  return result;
};

// Helper function to create common error properties
export const createCommonErrorProperties = (
  fileName: string,
  file: { type: string; size: number },
  errorType: string,
  error: unknown,
  additionalProps: Record<string, unknown> = {}
): LogPayloadData => {
  const diagnostics = {
    ...extractUploadErrorDiagnostics(error),
    ...uploadContextProperties(error),
  };

  // Base error information
  const result: LogPayloadData = {
    // File information
    fileName,
    fileType: file.type,
    fileSize: file.size,

    // Upload context
    storageProvider: 'r2',
    errorType,

    // Error information
    errorMessage:
      error instanceof Error
        ? diagnosticString(error.message)
        : 'Unknown error',
    errorName:
      error instanceof Error ? diagnosticString(error.name) : 'Unknown',
    ...diagnostics,
  };

  // Add additional properties, avoiding duplicates
  Object.entries(additionalProps).forEach(([key, value]) => {
    if (
      ['string', 'number', 'boolean'].includes(typeof value) &&
      value !== ''
    ) {
      result[key] = typeof value === 'string' ? diagnosticString(value) : value;
    }
  });

  return result;
};

// Unified logging utility for upload operations (errors only)
export const createLogPayload = (
  event: string,
  data: LogPayloadData
): LogPayloadData | null => {
  const basePayload: LogPayloadData = {
    event,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    ...data,
  };

  // Only log in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    return basePayload;
  }
  return null;
};

export const logError = (event: string, data: LogPayloadData): void => {
  const payload = createLogPayload(event, data);
  if (payload) {
    console.error(`${event}:`, payload);
  }
};

export const logEvent = (event: string, data: LogPayloadData): void => {
  const payload = createLogPayload(event, data);
  if (payload && process.env.NODE_ENV !== 'production') {
    console.log(`${event}:`, payload);
  }
};

// Upload-specific error types for step tracking
export interface UploadErrorData {
  step: string;
  additionalContext: Record<string, unknown>;
}

export interface MultipartUploadErrorData extends UploadErrorData {
  uploadId?: string;
  partNumber?: number;
}

// Factory functions to create error objects
export const createUploadError = (
  message: string,
  step: string,
  additionalContext: Record<string, unknown> = {}
): Error & UploadErrorData => {
  const error = new Error(message) as Error & UploadErrorData;
  error.name = 'UploadError';
  error.step = step;
  error.additionalContext = additionalContext;
  return error;
};

export const createMultipartUploadError = (
  message: string,
  step: string,
  uploadId?: string,
  partNumber?: number,
  additionalContext: Record<string, unknown> = {}
): Error & MultipartUploadErrorData => {
  const error = new Error(message) as Error & MultipartUploadErrorData;
  error.name = 'MultipartUploadError';
  error.step = step;
  error.uploadId = uploadId;
  error.partNumber = partNumber;
  error.additionalContext = { uploadId, partNumber, ...additionalContext };
  return error;
};

// Type guards for error detection
export const isUploadError = (
  error: unknown
): error is Error & UploadErrorData =>
  error instanceof Error && 'step' in error && 'additionalContext' in error;

export const isMultipartUploadError = (
  error: unknown
): error is Error & MultipartUploadErrorData =>
  isUploadError(error) && 'uploadId' in error && 'partNumber' in error;

// Helper to create upload error properties with consistent structure
const createUploadErrorProperties = (
  file: File,
  error: unknown,
  additionalProps: Record<string, unknown> = {}
) => {
  // Extract step from custom error types
  let step = 'unknown';
  let additionalContext = {};

  if (isMultipartUploadError(error)) {
    step = error.step;
    additionalContext = error.additionalContext;
  }

  return createCommonErrorProperties(
    file.name,
    { type: file.type, size: file.size },
    'upload_error',
    error,
    {
      uploadStep: (error as any)?.uploadContext?.uploadStep ?? step,
      ...additionalContext,
      ...additionalProps,
    }
  );
};

// Main logging function for upload errors
export const logUploadError = (
  event: string,
  file: File,
  error: unknown,
  additionalProps: Record<string, unknown> = {}
) => {
  try {
    const errorProperties = createUploadErrorProperties(
      file,
      error,
      additionalProps
    );
    // Keep the exception's outward message/name, without attached request graphs.
    const exception = new Error(errorProperties.errorMessage);
    exception.name = errorProperties.errorName ?? 'Error';
    const stack = diagnosticString((error as any)?.stack);
    if (stack) exception.stack = stack;
    try {
      logError(event, errorProperties);
    } catch {}
    void Promise.resolve(
      trackAppInsightsError(event, exception, errorProperties)
    ).catch(() => {});
  } catch {}
};

// Helper to create upload start properties
const createUploadStartProperties = (
  file: File,
  additionalProps: Record<string, unknown> = {}
): LogPayloadData => {
  return {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    ...additionalProps,
  };
};

export const logUploadEvent = (
  event: string,
  file: File,
  additionalProps: Record<string, unknown> = {}
) => {
  try {
    const startProperties = createUploadStartProperties(file, additionalProps);
    try {
      logEvent(event, startProperties);
    } catch {}
    void Promise.resolve(trackAppInsightsEvent(event, startProperties)).catch(
      () => {}
    );
  } catch {}
};

/**
 * Creates multipart upload error with preserved response details from original error
 */
export const createMultipartErrorWithResponse = (
  originalError: unknown,
  message: string,
  step: string,
  uploadId?: string,
  partNumber?: number,
  additionalContext: Record<string, unknown> = {}
) => {
  const multipartError = createMultipartUploadError(
    message,
    step,
    uploadId,
    partNumber,
    additionalContext
  );

  const httpDetails = extractHttpErrorDetails(originalError);

  if (
    httpDetails.statusCode !== undefined ||
    httpDetails.responseBody ||
    httpDetails.statusText ||
    httpDetails.requestUrl
  ) {
    (multipartError as any).response = {
      status: httpDetails.statusCode,
      statusText: httpDetails.statusText,
      data: httpDetails.responseBody,
      headers: httpDetails.responseHeaders || {},
      url: httpDetails.requestUrl,
    };
  }

  return multipartError;
};

type ConnectionMonitor = {
  isOnline: boolean;
  connectionType: string | null;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
};

type XhrDiagnostics = {
  phase: 'error' | 'timeout' | 'abort';
  method: string;
  url: string;
  responseURL?: string;
  status: number;
  statusText: string;
  readyState: number;
  responseSnippet?: string;
  headers?: string;
  hadUploadProgress: boolean;
  uploadedBytes: number;
  corsLikely: boolean; // status===0 && !hadUploadProgress
  midstreamResetLikely: boolean; // status===0 && hadUploadProgress
  timing?: Partial<PerformanceResourceTiming> & { nextHopProtocol?: string };
  connectionInfo: ConnectionMonitor;
};

type XhrDiagnosticsError = Error & { diagnostics: XhrDiagnostics };

export function buildXhrDiagnosticObject({
  correlationId,
  hadUploadProgress,
  method,
  xhr,
  phase,
  url,
  uploadedBytes,
}: {
  correlationId: string;
  hadUploadProgress: boolean;
  method: 'PUT';
  xhr: XMLHttpRequest;
  phase: 'error' | 'timeout' | 'abort';
  url: string;
  uploadedBytes: number;
}): XhrDiagnosticsError {
  let headersStr: string | undefined;
  let responseSnippet: string | undefined;
  try {
    headersStr = xhr.getAllResponseHeaders() || undefined;
  } catch {}
  try {
    const txt = typeof xhr.responseText === 'string' ? xhr.responseText : '';
    responseSnippet = txt ? txt.slice(0, 512) : undefined;
  } catch {}

  // Performance timing (best-effort)
  let timing: XhrDiagnostics['timing'];
  try {
    const name = xhr.responseURL || url;
    const entries = performance.getEntriesByName(
      name,
      'resource'
    ) as PerformanceResourceTiming[];
    const last = entries?.[entries.length - 1];
    if (last) {
      timing = {
        nextHopProtocol: (last as any).nextHopProtocol,
        startTime: last.startTime,
        duration: last.duration,
        transferSize: last.transferSize,
        encodedBodySize: last.encodedBodySize,
        decodedBodySize: last.decodedBodySize,
      };
    }
  } catch {}

  const diag: XhrDiagnostics = {
    phase,
    method,
    url,
    responseURL: xhr.responseURL || undefined,
    status: xhr.status,
    statusText: xhr.statusText,
    readyState: xhr.readyState,
    responseSnippet,
    headers: headersStr,
    hadUploadProgress,
    uploadedBytes,
    corsLikely: xhr.status === 0 && !hadUploadProgress,
    midstreamResetLikely: xhr.status === 0 && hadUploadProgress,
    timing,
    connectionInfo: getConnectionInfo(),
  };

  const err = new Error(
    `Studio: Upload Error: [XHR ${phase}] correlationId=${correlationId} status=${xhr.status} readyState=${xhr.readyState} ` +
      `uploadProgress=${hadUploadProgress ? uploadedBytes : 0} url=${
        xhr.responseURL || url
      }`
  ) as Error & { diagnostics: XhrDiagnostics };
  err.diagnostics = diag;
  return err;
}

export function getConnectionInfo(): ConnectionMonitor {
  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    isOnline: navigator.onLine,
    connectionType: connection?.type || null,
    effectiveType: connection?.effectiveType || null,
    downlink: connection?.downlink || null,
    rtt: connection?.rtt || null,
    saveData: connection?.saveData || false,
  };
}

export function isXhrDiagnosticsError(
  error: unknown
): error is XhrDiagnosticsError {
  return error instanceof Error && 'diagnostics' in error;
}
